import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import '../styles/AboutUs.css';
import {
  ChevronRight,
  Play,
  Users,
  Zap,
  Shield,
  Camera,
  Film,
  Edit,
  Palette,
  Music,
  BarChart,
  Megaphone,
  Search,
  Calendar,
  UserPlus,
  Briefcase,
  Scissors,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Nav from '../components/nav';
// import Bg from '../assets/img/c4.png';
import { baseUrl } from '../config';



const FeatureCard = ({ icon: Icon, title, description, imageUrl }) => (
  <div className="feature-card tilt-effect">
    <div className="card-content">
      <img src={imageUrl} alt={title} className="w-24 h-24 mb-4 rounded-full" />
      <Icon className="feature-icon w-12 h-12 mb-4" />
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description hidden group-hover:block">{description}</p>
    </div>
  </div>
);


const SectionTitle = ({ title, subtitle }) => (
  <div className="text-center mb-12">
    <h2 className="section-title">{title}</h2>
    <p className="section-subtitle">{subtitle}</p>
  </div>
);

const StatCard = ({ number, label }) => (
  <div className="stat-card tilt-effect">
    <p className="stat-number">{number}</p>
    <p className="stat-label">{label}</p>
  </div>
);
const AdvancedFeatureCard = ({ icon: Icon, title, description, imageUrl }) => (
  <div className="feature-card">
    <img src={imageUrl} alt={title} className="mx-auto mb-4 rounded-full" />
    <Icon className="text-primary mx-auto mb-2" size={32} />
    <h3 className="text-lg font-semibold">{title}</h3>
    <div className="description">{description}</div>
  </div>
);

const AboutUs = () => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
const [bgImage, setBgImage] = useState(null);

// Lazy load the background image
useEffect(() => {
  // Create a placeholder low-res image or color to show initially
  const placeholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E';
  
  // Load the actual image in the background
  const img = new Image();
  const bgPath = require('../assets/img/c4.png'); // Using require for webpack compatibility
  
  img.src = bgPath;
  img.onload = () => {
    setBgImage(bgPath);
    setIsImageLoaded(true);
  };

  // Set initial placeholder
  setBgImage(placeholder);
  
  // Optional: if image hasn't loaded after 3 seconds, show it anyway
  const timer = setTimeout(() => {
    if (!isImageLoaded) {
      setBgImage(bgPath);
      setIsImageLoaded(true);
    }
  }, 3000);
  
  return () => clearTimeout(timer);
}, []);
  return (
    <>
      <SEO
        title="About Us"
        description="Learn about ProducerBuddy's mission to empower filmmakers worldwide with AI-driven tools covering every stage of film production — from script to screen."
        url="https://producerbuddy.pilomation.com/AboutUS"
      />
      <Nav /> {/* Include the Nav component */}
      {/* Hero Section */}
      <section className="hero-section parallax-section">
      {/* Background image with lazy loading */}
      <div 
        className={`absolute inset-0 transition-opacity duration-1000 ${isImageLoaded ? 'opacity-60' : 'opacity-20'}`}
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 1
        }}
      ></div>
      
      {/* Loading indicator (optional) */}
      {!isImageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-2">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-r from-teal-700 to-transparent opacity-30"
        style={{ zIndex: 2 }}
      ></div>
      
      {/* Glass effect overlay */}
      <div 
        className="absolute inset-0 glass-effect"
        style={{ zIndex: 3 }}
      ></div>
      
      {/* Content container with glass effect */}
      <div className="container relative mx-auto px-6 z-10">
        <div className="glass-card max-w-2xl mx-auto p-8 rounded-xl">
          <div className="text-center hero-content">
            <h1 className="hero-title">Welcome to Producer's Buddy</h1>
            <p className="hero-subtitle">Revolutionizing filmmaking with NextGen AI</p>
            <div className="flex justify-center space-x-4">
              <Link to="/Services" className="hero-button hero-button-primary">
                Get Started
              </Link>
              <Link to="/howto" className="hero-button hero-button-secondary">
                Explore Features
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

      {/* Introduction */}
      <section className="section parallax-section">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="section-title">Transforming the Future of Filmmaking</h2>
            <p className="text-xl text-teal-600 mb-8">
              Producer's Buddy, a groundbreaking product of Pilomation, is at the forefront of the film industry's digital revolution. By harnessing the power of NextGen AI, we're not just changing how films are made—we're redefining the entire creative process.
            </p>
            <p className="text-xl text-teal-600">
              From pre-production to distribution, our all-in-one platform empowers filmmakers with cutting-edge tools, fostering collaboration and unleashing creativity like never before.
            </p>
          </div>
        </div>
      </section>

      { /* Mission and Vision */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-6">
          <div className="space-y-12">
            {/* Mission Section */}
            <div className="bg-white shadow-lg rounded-lg p-8">
              <h3 className="text-3xl font-bold mb-4 text-teal-700 animate-fade-in">
                Our Mission
              </h3>
              <p className="text-lg text-teal-600 animate-fade-in delay-300">
                To democratize filmmaking by providing accessible, AI-powered tools that empower creators at every level to bring their visions to life with unprecedented ease and efficiency.
              </p>
            </div>

            {/* Vision Section */}
            <div className="bg-white shadow-lg rounded-lg p-8">
              <h3 className="text-3xl font-bold mb-4 text-teal-700 animate-fade-in">
                Our Vision
              </h3>
              <p className="text-lg text-teal-600 animate-fade-in delay-300">
                To create a global community where storytellers of all backgrounds can collaborate, innovate, and push the boundaries of cinematic art, driven by the seamless integration of technology and creativity.
              </p>
            </div>
          </div>
        </div>

        {/* Tailwind CSS Animation Styles */}
        <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
          opacity: 0;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }
      `}</style>
      </section>

      {/* Why Choose Us */}
      <section className="section parallax-section">
        <div className="container mx-auto px-6">
          <SectionTitle
            title="Why Choose Producer's Buddy?"
            subtitle="Unparalleled advantages that set us apart in the industry"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link to="/AIintregationau">
              <FeatureCard
                icon={Zap}
                title="Cutting-Edge AI Integration"
                description="Harness the power of NextGen AI to streamline your workflow and enhance creativity"
                imageUrl="https://via.placeholder.com/150"
              />
            </Link>
            <Link to="/collaborative-ecosystem">
              <FeatureCard
                icon={Users}
                title="Collaborative Ecosystem"
                description="Connect with a global network of film professionals and resources"
                imageUrl="https://via.placeholder.com/150"
              />
            </Link>
            <Link to="/end-to-end-solution">
              <FeatureCard
                icon={Shield}
                title="End-to-End Solution"
                description="Manage your entire production lifecycle on a single, intuitive platform"
                imageUrl="https://via.placeholder.com/150"
              />
            </Link>
            <Link to="/all-in-one-tools">
              <FeatureCard
                icon={Film}
                title="All-in-One Production Tools"
                description="From script to screen, handle every aspect of filmmaking with ease"
                imageUrl="https://via.placeholder.com/150"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Product & Features */}
      <section className="section bg-gray-50 parallax-section">
        <div className="container mx-auto px-6">
          <SectionTitle
            title="Comprehensive Filmmaking Solutions"
            subtitle="Producer's Buddy offers a suite of tools for every stage of your film project"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={Edit}
              title="Pre-Production"
              description="AI-powered script analysis, budgeting, and scheduling tools"
              imageUrl="https://via.placeholder.com/150"
            />
            <FeatureCard
              icon={Camera}
              title="Production"
              description="Advanced casting, resource management, and progress tracking"
              imageUrl="https://via.placeholder.com/150"
            />
            <FeatureCard
              icon={Scissors}
              title="Post-Production"
              description="Collaborative editing, VFX, and sound design platforms"
              imageUrl="https://via.placeholder.com/150"
            />
            <FeatureCard
              icon={Megaphone}
              title="Distribution & Marketing"
              description="Data-driven market analysis and campaign management"
              imageUrl="https://via.placeholder.com/150"
            />
          </div>
        </div>
      </section>

      {/* AI and Innovation */}
      <section className="section bg-gradient-to-br from-[#A5E6E0] via-[#F5D4C0] to-[#E0BBE4] text-gray-800 parallax-section">
        <div className="container mx-auto px-6">
          <SectionTitle
            title="Pioneering the Future with NextGen AI"
            subtitle="Cutting-edge artificial intelligence to revolutionize your filmmaking process"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#C7CEEA]/60 backdrop-blur-sm p-6 rounded-xl shadow-xl tilt-effect">
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">Intelligent Script Analysis</h3>
              <p className="text-gray-700">Our AI analyzes scripts for pacing, character development, and market potential, providing invaluable insights to refine your storytelling.</p>
            </div>
            <div className="bg-[#FFDAB9]/60 backdrop-blur-sm p-6 rounded-xl shadow-xl tilt-effect">
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">Predictive Analytics</h3>
              <p className="text-gray-700">Leverage AI-driven insights to predict market trends, optimize release strategies, and maximize your film's success potential.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section parallax-section">
        <div className="container mx-auto px-6 text-center">
          <h2 className="cta-title">Ready to Revolutionize Your Filmmaking?</h2>
          <p className="cta-subtitle">Join thousands of filmmakers who are already using Producer's Buddy to bring their visions to life.</p>
          <div className="flex justify-center space-x-4">
            <Link to="/Services" className="cta-button cta-button-primary">
              Start Your Journey <ChevronRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/demo" className="cta-button cta-button-secondary">
              Watch Demo <Play className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="section bg-gray-50 parallax-section feature-section">
        <div className="container mx-auto px-6">
          <SectionTitle
            title="Unlock More Possibilities"
            subtitle="Explore our range of advanced features"
          />
          <div className="feature-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <AdvancedFeatureCard
              icon={Music}
              title="Sound Design"
              description="Gain access to an extensive library of royalty-free music and sound effects to elevate your creative projects. Whether you need cinematic scores or ambient tones, find everything you need to create a perfect soundscape."
              imageUrl="https://via.placeholder.com/150"
            />
            <AdvancedFeatureCard
              icon={Palette}
              title="Visual Effects"
              description="Transform your projects into visually stunning masterpieces with our intuitive VFX tools. Add professional-grade effects like transitions, overlays, and animations with ease."
              imageUrl="https://via.placeholder.com/150"
            />
            <AdvancedFeatureCard
              icon={BarChart}
              title="Analytics Dashboard"
              description="Monitor your project's progress with real-time analytics. Gain insights into performance metrics, user engagement, and actionable data to optimize your workflow."
              imageUrl="https://via.placeholder.com/150"
            />
            <AdvancedFeatureCard
              icon={Search}
              title="Market Research"
              description="Utilize our advanced tools to conduct in-depth market research. Discover audience preferences, emerging trends, and competitive insights to make data-driven decisions."
              imageUrl="https://via.placeholder.com/150"
            />
          </div>
        </div>

        <style jsx>{`
    .feature-grid .feature-card {
      position: relative;
      overflow: hidden;
      text-align: center;
      padding: 16px;
      border-radius: 8px;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }
    .feature-grid .feature-card:hover {
      transform: scale(1.05);
      box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2);
    }
    .feature-card .description {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: rgba(0, 0, 0, 0.7);
      color: white;
      opacity: 0;
      padding: 12px;
      transition: opacity 0.3s ease;
    }
    .feature-card:hover .description {
      opacity: 1;
    }
  `}</style>
      </section>




      {/* Community Engagement */}
      <section className="section parallax-section">
        <div className="container mx-auto px-6">
          <SectionTitle
            title="Join Our Community"
            subtitle="Connect, Collaborate, Create"
          />
          <div className="text-center mb-8">
            <Link to="/comunity" className="cta-button cta-button-primary">
              Join Now <ChevronRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={UserPlus}
              title="Networking Opportunities"
              description="Meet fellow filmmakers and industry professionals."
              imageUrl='https://via.placeholder.com/150'
            />
            <FeatureCard
              icon={Briefcase}
              title="Workshops & Training"
              description="Enhance your skills with our expert-led sessions."
              imageUrl='https://via.placeholder.com/150'
            />
            <FeatureCard
              icon={Calendar}
              title="Events & Screenings"
              description="Stay updated on upcoming film events and screenings."
              imageUrl='https://via.placeholder.com/150'
            />
          </div>
        </div>
      </section>

      {/* Awards and Recognition */}
      {/* <section className="section bg-teal-700 text-white parallax-section">
        <div className="container mx-auto px-6">
          <SectionTitle 
            title="Awards & Recognition" 
            subtitle="Celebrating Our Achievements"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StatCard number="10+" label="Awards Won" />
            <StatCard number="200+" label="Positive Reviews" />
            <StatCard number="300+" label="Partnerships Established" />
          </div>
        </div>
      </section> */}
    </>
  );
};

export default AboutUs;
